import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as PieTooltip,
  Legend as PieLegend,
} from "recharts";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer as LineResponsiveContainer,
} from "recharts";
const TransactionDashboard = () => {
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  // Year and Month filter state - Default to current month
  const currentYear = new Date().getFullYear();
  const currentMonth = (new Date().getMonth() + 1).toString();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  // Searchable dropdown state
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [monthSearch, setMonthSearch] = useState("");
  const dropdownRef = useRef(null);
  // Dashboard data state - Added new fields
  const [dashboardData, setDashboardData] = useState({
    totalIncome: 0,
    totalExpense: 0,
    totalVendorPaid: 0,
    vehicleIncome: 0,
    vehiclePending: 0,
    totalServiceCars: 0,
    totalPurchases: 0,
    totalOutstanding: 0,
    totalVehicles: 0,
    totalCustomers: 0,
    netBalance: 0,
    topVendors: [],
    expenseByCategory: [],
    monthlyData: [],
    dailyIncomeExpense: [],
    dailyServiceCars: [],
    lastTransactions: [],
    pendingServices: [],
  });
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  // Month options
  const monthOptions = [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];
  // Year options (last 5 years + current + next year)
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);
  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMonthOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // Fetch dashboard data on mount and filter change
  useEffect(() => {
    fetchDashboardData();
  }, [selectedYear, selectedMonth]);
  const fetchDashboardData = async () => {
    setFilterLoading(true);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("transaction_grage_id", grage_id);
      formData.append("year", selectedYear);
      if (selectedMonth) {
        formData.append("month", selectedMonth);
      }
      formData.append("action", "dashboard_data");
      const response = await axios.post(
        `${server_url}transaction_dashboard.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.status === "1") {
        const data = response.data.result;
        setDashboardData({
          totalIncome: parseFloat(data.total_income || 0),
          totalExpense: parseFloat(data.total_expense || 0),
          totalVendorPaid: parseFloat(data.total_vendor_paid || 0),
          vehicleIncome: parseFloat(data.vehicle_income || 0),
          vehiclePending: parseFloat(data.vehicle_pending || 0),
          totalServiceCars: parseInt(data.total_service_cars || 0),
          totalPurchases: parseFloat(data.total_purchases || 0),
          totalOutstanding: parseFloat(data.total_outstanding || 0),
          totalVehicles: parseInt(data.total_vehicles || 0),
          totalCustomers: parseInt(data.total_customers || 0),
          netBalance: parseFloat(data.net_balance || 0),
          topVendors: data.top_vendors || [],
          expenseByCategory: data.expense_by_category || [],
          monthlyData: data.monthly_data || [],
          dailyIncomeExpense: data.daily_income_expense || [],
          dailyServiceCars: data.daily_service_cars || [],
          lastTransactions: data.last_transactions || [],
          pendingServices: data.pending_services || [],
        });
      } else {
        toast.error(response.data.error || "Unable to fetch dashboard data");
      }
    } catch (error) {
      console.error("Fetch dashboard data error:", error);
      toast.error("Unable to fetch dashboard data");
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };
  const formatCurrency = (amount) => {
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num);
  };
  const getRandomColor = () => {
    const colors = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#8884D8",
      "#82CA9D",
      "#FFC658",
      "#8DD1E1",
      "#A4DE6C",
      "#D0ED57",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  const getMonthName = (monthNumber) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months[monthNumber - 1] || `Month ${monthNumber}`;
  };
  const getSelectedMonthLabel = () => {
    if (!selectedMonth) return "All Months";
    const month = monthOptions.find((m) => m.value === selectedMonth);
    return month ? month.label : "All Months";
  };
  const filteredMonthOptions = monthOptions.filter((option) =>
    option.label.toLowerCase().includes(monthSearch.toLowerCase())
  );
  const handleMonthSelect = (value) => {
    setSelectedMonth(value);
    setIsMonthOpen(false);
    setMonthSearch("");
  };
  const handleClearFilters = () => {
    setSelectedYear(currentYear);
    setSelectedMonth("");
  };
  const getTransactionTypeLabel = (type) => {
    return type === "1" ? "Income" : "Expense";
  };
  // Function to fill missing days with zeros
  const fillMissingDays = (data, isServiceCars = false) => {
    if (!selectedMonth) return data;
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const fullData = Array.from({ length: daysInMonth }, (_, i) => ({
      day: (i + 1).toString(),
      ...(isServiceCars ? { cars: 0 } : { income: 0, expense: 0 }),
    }));
    data.forEach((item) => {
      const dayNum = new Date(item.day).getDate().toString();
      const index = fullData.findIndex((d) => d.day === dayNum);
      if (index !== -1) {
        if (isServiceCars) {
          fullData[index].cars = parseInt(item.total_cars || 0);
        } else {
          fullData[index].income = parseFloat(item.total_income || 0);
          fullData[index].expense = parseFloat(item.total_expense || 0);
        }
      }
    });
    return fullData;
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: "10px",
      }}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            color: "#666",
            fontSize: "16px",
          }}
        >
          Loading dashboard data...
        </div>
      ) : (
        <>
          {/* Header with Filters and Search Button */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto auto",
                gap: "15px",
                alignItems: "end",
              }}
            >
               {/* Month Searchable Dropdown */}
              <div ref={dropdownRef}>
                <div style={{ position: "relative" }}>
                  <div
                    onClick={() => setIsMonthOpen(!isMonthOpen)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "13px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{getSelectedMonthLabel()}</span>
                    <i
                      className={`fas fa-chevron-${
                        isMonthOpen ? "up" : "down"
                      }`}
                      style={{ fontSize: "12px" }}
                    ></i>
                  </div>
                  {isMonthOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 1000,
                        marginTop: "2px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      {/* Search Input */}
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <input
                          type="text"
                          value={monthSearch}
                          onChange={(e) => setMonthSearch(e.target.value)}
                          placeholder="Search month..."
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Month Options */}
                      {filteredMonthOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => handleMonthSelect(option.value)}
                          style={{
                            padding: "10px 12px",
                            cursor: "pointer",
                            backgroundColor:
                              selectedMonth === option.value
                                ? "#f0f7ff"
                                : "white",
                            color:
                              selectedMonth === option.value
                                ? "#0352a1"
                                : "#333",
                            fontSize: "13px",
                            borderBottom: "1px solid #f5f5f5",
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Year Selector */}
              <div>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "13px",
                    backgroundColor: "white",
                  }}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
             
              {/* Search Button */}
              <button
                onClick={fetchDashboardData}
                disabled={filterLoading}
                style={{
                  padding: "12px",
                  backgroundColor: "#0352a1",
                  color: "white",
                  border: "none",
                  borderRadius: "50px",
                  fontSize: "13px",
                  cursor: filterLoading ? "not-allowed" : "pointer",
                }}
              >
                {filterLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Loading...
                  </>
                ) : (
                  <i className="fas fa-search"></i>
                )}
              </button>
            </div>
            {filterLoading && (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Loading...
                </span>
              </div>
            )}
          </div>
          {/* Summary Cards - First Row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            {[
              {
                title: "Total Vehicles",
                value: formatNumber(dashboardData.totalVehicles),
                icon: "fas fa-car",
                bgColor: "#dbeafe",
                borderColor: "#3b82f6",
                textColor: "#1e40af",
                iconBg: "#3b82f6",
                iconText: "fas fa-car",
              },
              {
                title: "Total Customers",
                value: formatNumber(dashboardData.totalCustomers),
                icon: "fas fa-users",
                bgColor: "#f0fdf4",
                borderColor: "#16a34a",
                textColor: "#15803d",
                iconBg: "#16a34a",
                iconText: "fas fa-users",
              },
              {
                title: "Income",
                value: formatCurrency(dashboardData.totalIncome),
                icon: "fas fa-arrow-down",
                bgColor: "#d1fae5",
                borderColor: "#10b981",
                textColor: "#065f46",
                iconBg: "#10b981",
                iconText: "fas fa-rupee-sign",
              },
              {
                title: "Expense",
                value: formatCurrency(dashboardData.totalExpense),
                icon: "fas fa-arrow-up",
                bgColor: "#fee2e2",
                borderColor: "#dc2626",
                textColor: "#991b1b",
                iconBg: "#dc2626",
                iconText: "fas fa-rupee-sign",
              },
              {
                title: "Vendor Paid",
                value: formatCurrency(dashboardData.totalVendorPaid),
                icon: "fas fa-arrow-up",
                bgColor: "#fee2e2",
                borderColor: "#dc2626",
                textColor: "#991b1b",
                iconBg: "#dc2626",
                iconText: "fas fa-rupee-sign",
              },
              {
                title: "Vendor Outstanding",
                value: formatCurrency(dashboardData.totalOutstanding),
                icon: "fas fa-exclamation-triangle",
                bgColor: "#fef3c7",
                borderColor: "#f59e0b",
                textColor: "#92400e",
                iconBg: "#f59e0b",
                iconText: "fas fa-wallet",
              },
              {
                title: "Total Purchases",
                value: formatCurrency(dashboardData.totalPurchases),
                icon: "fas fa-shopping-cart",
                bgColor: "#e0f2fe",
                borderColor: "#0ea5e9",
                textColor: "#075985",
                iconBg: "#0ea5e9",
                iconText: "fas fa-cart-plus",
              },
              {
                title: "Service Cars",
                value: formatNumber(dashboardData.totalServiceCars),
                icon: "fas fa-car",
                bgColor: "#dbeafe",
                borderColor: "#3b82f6",
                textColor: "#1e40af",
                iconBg: "#3b82f6",
                iconText: "fas fa-car",
              },
              {
                title: "Service Income",
                value: formatCurrency(dashboardData.vehicleIncome),
                icon: "fas fa-arrow-down",
                bgColor: "#d1fae5",
                borderColor: "#10b981",
                textColor: "#065f46",
                iconBg: "#10b981",
                iconText: "fas fa-rupee-sign",
              },
              {
                title: "Service Outstanding",
                value: formatCurrency(dashboardData.vehiclePending),
                icon: "fas fa-arrow-up",
                bgColor: "#fee2e2",
                borderColor: "#dc2626",
                textColor: "#991b1b",
                iconBg: "#dc2626",
                iconText: "fas fa-rupee-sign",
              },
            ].map((card, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: card.bgColor,
                  padding: "15px",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${card.borderColor}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <p
                      style={{
                        margin: "0 0 5px 0",
                        color: card.textColor,
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      <i
                        className={card.icon}
                        style={{ marginRight: "5px" }}
                      ></i>
                      {card.title}
                    </p>
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        margin: 0,
                        color: card.textColor,
                      }}
                    >
                      {card.value}
                    </p>
                  </div>
                  <div
                    style={{
                      backgroundColor: card.iconBg,
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={card.iconText}
                      style={{ color: "white", fontSize: "14px" }}
                    ></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Net Balance Card */}
          <div
            style={{
              backgroundColor:
                dashboardData.netBalance >= 0 ? "#ecfdf5" : "#fef2f2",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "15px",
              borderLeft: `4px solid ${
                dashboardData.netBalance >= 0 ? "#10b981" : "#dc2626"
              }`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <p
                  style={{
                    margin: "0 0 5px 0",
                    color:
                      dashboardData.netBalance >= 0 ? "#065f46" : "#991b1b",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  <i
                    className="fas fa-balance-scale"
                    style={{ marginRight: "5px" }}
                  ></i>
                  Net Balance
                </p>
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    margin: 0,
                    color:
                      dashboardData.netBalance >= 0 ? "#065f46" : "#991b1b",
                  }}
                >
                  {formatCurrency(dashboardData.netBalance)}
                </p>
              </div>
              <div
                style={{
                  backgroundColor:
                    dashboardData.netBalance >= 0 ? "#10b981" : "#dc2626",
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fas fa-rupee-sign"
                  style={{ color: "white", fontSize: "16px" }}
                ></i>
              </div>
            </div>
          </div>
          {/* Expense by Category Line Chart */}
          {dashboardData.expenseByCategory.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "15px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 15px 0",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i
                  className="fas fa-chart-line"
                  style={{ color: "#0352a1" }}
                ></i>
                Expense by Category
              </h3>
              <div style={{ height: "250px", marginLeft: "-25px" }}>
                <LineResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dashboardData.expenseByCategory.map((cat) => ({
                      category: cat.category_name,
                      expense: parseFloat(cat.total_amount || 0),
                    }))}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="category"
                      fontSize={11}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="expense"
                      name="Expense"
                      stroke="#dc2626"
                      strokeWidth={2}
                      dot={{ fill: "#dc2626", strokeWidth: 2 }}
                    />
                  </LineChart>
                </LineResponsiveContainer>
              </div>
            </div>
          )}
          {/* Monthly Income & Expense Line Chart */}
          {dashboardData.monthlyData.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "15px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 15px 0",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i
                  className="fas fa-chart-line"
                  style={{ color: "#0352a1" }}
                ></i>
                Monthly Income vs Expense ({selectedYear})
              </h3>
              <div style={{ height: "250px", marginLeft: "-25px" }}>
                <LineResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dashboardData.monthlyData.map((month) => ({
                      month: getMonthName(month.month_number),
                      income: parseFloat(month.total_income || 0),
                      expense: parseFloat(month.total_expense || 0),
                    }))}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="income"
                      name="Income"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: "#10b981", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="expense"
                      name="Expense"
                      stroke="#dc2626"
                      strokeWidth={2}
                      dot={{ fill: "#dc2626", strokeWidth: 2 }}
                    />
                  </LineChart>
                </LineResponsiveContainer>
              </div>
            </div>
          )}
          {/* Daily Income & Expense Line Chart (if month selected) */}
          {selectedMonth && dashboardData.dailyIncomeExpense.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "15px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 15px 0",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i
                  className="fas fa-chart-line"
                  style={{ color: "#0352a1" }}
                ></i>
                Daily Income vs Expense ({getSelectedMonthLabel()}{" "}
                {selectedYear})
              </h3>
              <div style={{ height: "250px", marginLeft: "-25px" }}>
                <LineResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={fillMissingDays(dashboardData.dailyIncomeExpense)}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="income"
                      name="Income"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: "#10b981", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="expense"
                      name="Expense"
                      stroke="#dc2626"
                      strokeWidth={2}
                      dot={{ fill: "#dc2626", strokeWidth: 2 }}
                    />
                  </LineChart>
                </LineResponsiveContainer>
              </div>
            </div>
          )}
          {/* Daily Service Cars Line Chart (if month selected) */}
          {selectedMonth && dashboardData.dailyServiceCars.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "15px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 15px 0",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i
                  className="fas fa-chart-line"
                  style={{ color: "#0352a1" }}
                ></i>
                Daily Service Cars ({getSelectedMonthLabel()} {selectedYear})
              </h3>
              <div style={{ height: "250px", marginLeft: "-25px" }}>
                <LineResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={fillMissingDays(dashboardData.dailyServiceCars, true)}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cars"
                      name="Service Cars"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </LineResponsiveContainer>
              </div>
            </div>
          )}
          {/* Last 20 Transactions Table */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <h3
              style={{
                margin: "0 0 15px 0",
                color: "#333",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fas fa-list" style={{ color: "#0352a1" }}></i>
              Last 20 Transactions (Income & Expense)
            </h3>
            {dashboardData.lastTransactions.length > 0 ? (
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.lastTransactions.map((trans, index) => (
                      <tr
                        key={trans.transaction_id || index}
                        style={{
                          borderBottom: "1px solid #f1f1f1",
                          backgroundColor:
                            index % 2 === 0 ? "white" : "#fafafa",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 8px",
                            color: "#333",
                            fontSize: "11px",
                          }}
                        >
                          {new Date(
                            trans.transaction_date
                          ).toLocaleDateString()}
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            color: "#333",
                            fontWeight: "500",
                          }}
                        >
                          {getTransactionTypeLabel(trans.transaction_type)}
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            textAlign: "right",
                            color:
                              trans.transaction_type === "1"
                                ? "#10b981"
                                : "#dc2626",
                            fontWeight: "bold",
                            fontSize: "13px",
                          }}
                        >
                          {formatCurrency(trans.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <i
                  className="fas fa-list"
                  style={{
                    fontSize: "32px",
                    marginBottom: "10px",
                    opacity: 0.5,
                  }}
                ></i>
                <p style={{ fontSize: "13px", margin: 0 }}>
                  No transactions found
                </p>
              </div>
            )}
          </div>
          {/* Top 10 Vendors Outstanding Table */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="fas fa-users" style={{ color: "#0352a1" }}></i>
                Top 10 Vendors - Outstanding
              </h3>
              <Link
                to="/vendors"
                style={{
                  color: "#0352a1",
                  textDecoration: "none",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                View All <i className="fas fa-external-link-alt"></i>
              </Link>
            </div>
            {dashboardData.topVendors.length > 0 ? (
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Vendor Name
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Outstanding
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.topVendors.map((vendor, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom: "1px solid #f1f1f1",
                          backgroundColor:
                            index % 2 === 0 ? "white" : "#fafafa",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 8px",
                            color: "#333",
                            fontWeight: "500",
                          }}
                        >
                          <div>
                            <div
                              style={{ fontWeight: "600", marginBottom: "2px" }}
                            >
                              {vendor.vendor_name ||
                                vendor.vendor_firm_name ||
                                ""}
                            </div>
                            {vendor.vendor_mobile && (
                              <div style={{ fontSize: "11px", color: "#666" }}>
                                {vendor.vendor_mobile}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            color:
                              vendor.outstanding_amount > 0
                                ? "#dc2626"
                                : "#10b981",
                            fontWeight: "bold",
                            fontSize: "13px",
                          }}
                        >
                          {formatCurrency(vendor.outstanding_amount)}
                        </td>
                        <td
                          style={{ padding: "10px 8px", textAlign: "center" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "center",
                            }}
                          >
                            {vendor.vendor_mobile && (
                              <a
                                href={`tel:${vendor.vendor_mobile}`}
                                style={{
                                  color: "#10b981",
                                  textDecoration: "none",
                                  fontSize: "11px",
                                  padding: "4px 8px",
                                  border: "1px solid #10b981",
                                  borderRadius: "4px",
                                }}
                              >
                                <i className="fas fa-phone rotate90"></i>
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <i
                  className="fas fa-users"
                  style={{
                    fontSize: "32px",
                    marginBottom: "10px",
                    opacity: 0.5,
                  }}
                ></i>
                <p style={{ fontSize: "13px", margin: 0 }}>
                  No vendor outstanding data
                </p>
              </div>
            )}
          </div>
          {/* Pending Services Table */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="fas fa-tools" style={{ color: "#0352a1" }}></i>
                Pending Payment Services
              </h3>
              <Link
                to="/history"
                style={{
                  color: "#0352a1",
                  textDecoration: "none",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                View All <i className="fas fa-external-link-alt"></i>
              </Link>
            </div>
            {dashboardData.pendingServices.length > 0 ? (
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Vehicle
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Customer
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Entry Date
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Rs
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          color: "#666",
                          fontWeight: "600",
                          fontSize: "11px",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.pendingServices
                      .slice(0, 10)
                      .map((service, index) => (
                        <tr
                          key={service.vehicle_entry_id || index}
                          style={{
                            borderBottom: "1px solid #f1f1f1",
                            backgroundColor:
                              index % 2 === 0 ? "white" : "#fafafa",
                          }}
                        >
                          <td
                            style={{
                              padding: "10px 8px",
                              color: "#333",
                              fontWeight: "500",
                            }}
                          >
                            {service.vehicle_number || ""}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              color: "#333",
                              fontWeight: "500",
                            }}
                          >
                            {service.customer_name || ""}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              color: "#666",
                              fontSize: "11px",
                              textAlign: "center",
                            }}
                          >
                            {new Date(
                              service.vehicle_entry_date
                            ).toLocaleDateString()}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              color: "#333",
                              fontWeight: "500",
                            }}
                          >
                            {formatCurrency(service.vehicle_entry_total_amt) ||
                              "0"}
                          </td>
                          <td
                            style={{ padding: "10px 8px", textAlign: "center" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center",
                              }}
                            >
                              {service.customer_mobile && (
                                <>
                                  <a
                                    href={`tel:${service.customer_mobile}`}
                                    style={{
                                      color: "#10b981",
                                      textDecoration: "none",
                                      fontSize: "11px",
                                      padding: "4px 8px",
                                      border: "1px solid #10b981",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <i className="fas fa-phone rotate90"></i>
                                  </a>
                                  <a
                                    href={`https://wa.me/91${service.customer_mobile.replace(
                                      /[^0-9]/g,
                                      ""
                                    )}?text=Hello ${
                                      service.customer_name
                                    },%0A%0AThis is a gentle reminder regarding the pending payment for your vehicle service.%0A%0A🚗 Vehicle No: ${
                                      service.vehicle_number
                                    }%0A📅 Service Date: ${new Date(
                                      service.vehicle_entry_date
                                    ).toLocaleDateString()}%0A💰 Amount Due: ${
                                      formatCurrency(
                                        service.vehicle_entry_total_amt
                                      ) || "0"
                                    }%0A%0AKindly complete the payment at your convenience.%0AThank you 🙏`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#25D366",
                                      textDecoration: "none",
                                      fontSize: "11px",
                                      padding: "4px 8px",
                                      border: "1px solid #25D366",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <i className="fab fa-whatsapp"></i>
                                  </a>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <i
                  className="fas fa-tools"
                  style={{
                    fontSize: "32px",
                    marginBottom: "10px",
                    opacity: 0.5,
                  }}
                ></i>
                <p style={{ fontSize: "13px", margin: 0 }}>
                  No pending services
                </p>
              </div>
            )}
          </div>
        </>
      )}
      <style jsx>{`
        /* Mobile responsive grid */
        @media (max-width: 768px) {
          .grid-header {
            grid-template-columns: 1fr !important;
            gap: 10px;
          }
        }
        /* Scrollbar styling */
        div::-webkit-scrollbar {
          width: 4px;
        }
        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        div::-webkit-scrollbar-thumb {
          background: #0352a1;
          border-radius: 4px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: #024080;
        }
          .rotate90 {
          transform: rotateZ(90deg);
        }
      `}</style>
    </div>
  );
};
export default TransactionDashboard;
